import React, { useState, useEffect } from 'react'
import MainLayout from '../../layout/MainLayout'
import PackageCreateForm from '../../components/PackageCreateForm'
import PackageList from '../../components/PackageList';
import { api } from '../../api/axios';
import { Plus, RefreshCcw, ListChecksIcon, Sparkles, Archive, RotateCcw, X } from 'lucide-react';
import { toast } from 'react-toastify';

export default function Packages() {
    const [view, setView] = useState('list') // 'list' | 'create' | 'edit'
    const [editTarget, setEditTarget] = useState(null);
    const [packages, setPackages] = useState([]);
    const [isFetching, setIsFetching] = useState(false);
    const [isSeeding, setIsSeeding] = useState(false);
    const [error, setError] = useState(null);
    const [archivedOpen, setArchivedOpen] = useState(false);
    const [archived, setArchived] = useState([]);
    const [archivedLoading, setArchivedLoading] = useState(false);

    const openArchived = async () => {
        setArchivedOpen(true);
        setArchivedLoading(true);
        try {
            const res = await api.get('/packages/archived');
            setArchived(res.data || []);
        } catch (err) {
            console.error('Archived fetch error:', err);
            toast.error('Failed to load archived packages');
        } finally {
            setArchivedLoading(false);
        }
    };

    const restorePackage = async (id) => {
        if (!window.confirm('Restore this package?')) return;
        try {
            await api.patch(`/packages/${id}/restore`);
            toast.success('Package restored');
            setArchived(prev => prev.filter(p => p._id !== id));
            fetchPackages();
        } catch (err) {
            toast.error('Failed to restore');
        }
    };

    const handleEdit = (pkg) => {
        setEditTarget(pkg);
        setView('edit');
    };

    const handleToggleVisibility = async (pkg) => {
        try {
            await api.put(`/packages/${pkg._id}`, { hidden: !pkg.hidden });
            toast.success(pkg.hidden ? "Package unhidden" : "Package hidden");
            fetchPackages();
        } catch (err) {
            console.error(err);
            const detail = err?.response?.data?.message || err?.response?.data?.error || err?.message;
            toast.error(`Failed to toggle visibility${detail ? `: ${detail}` : ''}`);
        }
    };

    const handleDelete = async (pkg) => {
        if (!window.confirm(
            `Delete "${pkg.name}"?\n\nIt will be archived (recoverable from "Archives" within the last 20).`
        )) return;
        try {
            await api.delete(`/packages/${pkg._id}`);
            toast.success("Package archived");
            fetchPackages();
        } catch (err) {
            console.error(err);
            const detail = err?.response?.data?.message || err?.response?.data?.error || err?.message;
            toast.error(`Failed to delete${detail ? `: ${detail}` : ''}`);
        }
    };

    const seedDefaults = async () => {
        if (!window.confirm("Seed the 3 default packages (Free / Package 1 / Package 2)? This updates existing packages with the same name if present.")) return;
        setIsSeeding(true);
        try {
            const res = await api.post('/packages/seed-defaults');
            toast.success(`Seeded ${res.data.packages?.length ?? 3} packages`);
            fetchPackages();
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || "Failed to seed packages");
        } finally {
            setIsSeeding(false);
        }
    };

    useEffect(() => {
        fetchPackages();
    }, []);

    const fetchPackages = async () => {
        setIsFetching(true);
        setError(null);
        try {
            const res = await api.get("/packages");
            setPackages(res.data);
        } catch (err) {
            console.error("Error fetching packages:", err);
            setError('Failed to fetch packages. Please try again later.');
        } finally {
            setTimeout(() => {
                setIsFetching(false);
            }, 3000);
        }
    };

    return (
        <MainLayout title="Packages"
            Buttons={
                <div className="flex items-center gap-2">
                    <button
                        onClick={openArchived}
                        className="border border-gray-300 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 transition flex items-center gap-2"
                        title="View last 20 archived packages"
                    >
                        <Archive className="w-4 h-4" />
                        Archives
                    </button>
                    <button
                        onClick={seedDefaults}
                        disabled={isSeeding}
                        className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition flex items-center gap-2 disabled:opacity-60"
                        title="Upsert Free, Package 1, Package 2 with default settings"
                    >
                        <Sparkles className={`w-4 h-4 ${isSeeding ? 'animate-pulse' : ''}`} />
                        {isSeeding ? 'Seeding...' : 'Seed Defaults'}
                    </button>
                    <button onClick={fetchPackages} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2">
                        {isFetching ? <RefreshCcw className="inline w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="inline w-4 h-4 mr-2" />}
                        {isFetching ? 'Refreshing...' : 'Refresh Packages'}
                    </button>
                    <button
                        onClick={() => setView(view === "list" ? "create" : "list")}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition flex items-center gap-2"
                    >
                        {view === "list" ? <Plus className="inline w-4 h-4 mr-2" /> : <ListChecksIcon className="inline w-4 h-4 mr-2" />}
                        {view === "list" ? "Create Package" : "Back to List"}
                    </button>
                </div>
            }
        >

            {/* <div className="flex justify-between items-center mb-6 border-b pb-2">
                <h1 className="text-3xl font-bold text-gray-800">Manage Packages</h1>
                <div className="flex items-center gap-2">
                    <button onClick={fetchPackages} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2">
                        {isFetching ? <RefreshCcw className="inline w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="inline w-4 h-4 mr-2" />}
                        {isFetching ? 'Refreshing...' : 'Refresh Packages'}
                    </button>
                    <button
                        onClick={() => setView(view === "list" ? "create" : "list")}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition flex items-center gap-2"
                    >
                        {view === "list" ? <Plus className="inline w-4 h-4 mr-2" /> : <ListChecksIcon className="inline w-4 h-4 mr-2" />}
                        {view === "list" ? "Create Package" : "Back to List"}
                    </button>
                </div>
            </div> */}

            {view === "create" && (
                <PackageCreateForm
                    onDone={() => {
                        fetchPackages();
                        setView("list");
                    }}
                />
            )}
            {view === "edit" && (
                <PackageCreateForm
                    editData={editTarget}
                    onDone={() => {
                        fetchPackages();
                        setEditTarget(null);
                        setView("list");
                    }}
                />
            )}
            {view === "list" && (
                <PackageList
                    packages={packages}
                    onEdit={handleEdit}
                    onToggleVisibility={handleToggleVisibility}
                    onDelete={handleDelete}
                />
            )}

            {/* Archived packages modal */}
            {archivedOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                    onClick={() => setArchivedOpen(false)}
                >
                    <div
                        className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] flex flex-col overflow-hidden"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-5 py-4 border-b">
                            <div>
                                <h2 className="font-semibold text-lg">Archived Packages</h2>
                                <p className="text-xs text-gray-500">Last 20 deleted packages — restore or let them age out.</p>
                            </div>
                            <button onClick={() => setArchivedOpen(false)} className="p-2 hover:bg-gray-100 rounded-full">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto p-4">
                            {archivedLoading ? (
                                <div className="text-center text-gray-400 py-8">Loading…</div>
                            ) : archived.length === 0 ? (
                                <div className="text-center text-gray-400 py-8">No archived packages.</div>
                            ) : (
                                <ul className="space-y-2">
                                    {archived.map(pkg => (
                                        <li key={pkg._id} className="flex items-center justify-between border rounded-lg px-4 py-3">
                                            <div>
                                                <div className="font-medium text-gray-900">{pkg.name}</div>
                                                <div className="text-xs text-gray-500">
                                                    Archived {pkg.archivedAt ? new Date(pkg.archivedAt).toLocaleString() : '—'}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => restorePackage(pkg._id)}
                                                className="flex items-center gap-1.5 text-sm text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded"
                                            >
                                                <RotateCcw size={14} />
                                                Restore
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </MainLayout>
    )
}
